// src/pages/ContactUs/index.js
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  Form,
  FormGroup,
  Input,
  Button,
  Alert,
  Spinner,
} from "reactstrap";
import { Formik } from "formik";
import * as Yup from "yup";
import { sendContactMessage } from "../../helpers/eirna_backend_helper";
// Import the DNA image
import dnaImage from "../../assets/images/eirna/helpers/rna.png"; // Adjust path as needed

const ContactUs = () => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");

  // Validation schema
  const validationSchema = Yup.object({
    message: Yup.string()
      .min(10, "Message must be at least 10 characters")
      .required("Message is required"),
  });

  const handleSubmit = async (values, { setSubmitting, resetForm }) => {
    setIsLoading(true);
    setSuccessMessage("");
    setErrorMessage("");

    try {
      // Replace with your actual API call
      await sendContactMessage({ message: values.message });

      setSuccessMessage(
        "Message sent successfully! We'll get back to you soon."
      );
      resetForm();
    } catch (error) {
      setErrorMessage("Failed to send message. Please try again.");
    } finally {
      setIsLoading(false);
      setSubmitting(false);
    }
  };

  document.title = "Contact Us | EIRNABio Connect";

  return (
    <div className="page-content">
      <Container fluid>
        {/* Page Header */}
        <Row className="mb-4">
          <Col lg={12}>
            <h4 className="mb-0 fw-bold" style={{ color: "#374151" }}>
              Contact Us
            </h4>
          </Col>
        </Row>

        {/* Main Content */}
        <Row>
          <Col>
            <Card
              style={{
                backgroundColor: "#ECF4FE",
                border: "1px solid #e5e7eb",
                borderRadius: "2px",
                boxShadow: "0 1px 3px rgba(179, 178, 178, 0.1)",
              }}
            >
              {/* RNA Background Image - Bottom Left */}
              <div
                style={{
                  position: "absolute",
                  left: "-30px",
                  bottom: "-30px",
                  width: "200px",
                  height: "150px",
                  backgroundImage: `url(${dnaImage})`,
                  backgroundSize: "contain",
                  backgroundRepeat: "no-repeat",
                  backgroundPosition: "center",
                  opacity: 0.1,
                  zIndex: 1,
                }}
              ></div>

              <CardBody
                style={{ padding: "32px", position: "relative", zIndex: 2 }}
              >
                <Row>
                  {/* Left Side - Form */}
                  <Col lg={10} md={7}>
                    {/* Description */}
                    <div className="mb-4">
                      <p
                        style={{
                          fontSize: "15px",
                          lineHeight: "1.6",
                          color: "#6b7280",
                          marginBottom: "0",
                        }}
                      >
                        Bug reports or general inquiries can be sent to{" "}
                        <strong style={{ color: "#374151" }}>
                          ebconnect@eirnabio.com
                        </strong>{" "}
                        or by filling out the form below
                      </p>
                    </div>

                    {/* Alerts */}
                    {successMessage && (
                      <Alert
                        color="success"
                        className="mb-3"
                        style={{
                          backgroundColor: "#f0fdf4",
                          borderColor: "#bbf7d0",
                          color: "#166534",
                          borderRadius: "6px",
                          fontSize: "14px",
                        }}
                      >
                        <i className="ri-check-circle-line me-2"></i>
                        {successMessage}
                      </Alert>
                    )}

                    {errorMessage && (
                      <Alert
                        color="danger"
                        className="mb-3"
                        style={{
                          backgroundColor: "#fef2f2",
                          borderColor: "#fecaca",
                          color: "#dc2626",
                          borderRadius: "6px",
                          fontSize: "14px",
                        }}
                      >
                        <i className="ri-error-warning-line me-2"></i>
                        {errorMessage}
                      </Alert>
                    )}

                    {/* Form */}
                    <Formik
                      initialValues={{ message: "" }}
                      validationSchema={validationSchema}
                      onSubmit={handleSubmit}
                    >
                      {({
                        values,
                        errors,
                        touched,
                        handleChange,
                        handleBlur,
                        handleSubmit,
                        isSubmitting,
                      }) => (
                        <Form onSubmit={handleSubmit}>
                          <FormGroup className="mb-4">
                            <Input
                              type="textarea"
                              name="message"
                              placeholder="Message here"
                              value={values.message}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              invalid={errors.message && touched.message}
                              rows="8"
                              style={{
                                fontSize: "15px",
                                borderRadius: "6px",
                                border: "1px solid #d1d5db",
                                backgroundColor: "#f9fafb",
                                color: "#374151",
                                padding: "16px",
                                resize: "vertical",
                                minHeight: "200px",
                              }}
                              onFocus={(e) => {
                                e.target.style.borderColor = "#2F9E37";
                                e.target.style.backgroundColor = "#ffffff";
                                e.target.style.boxShadow =
                                  "0 0 0 3px rgba(47, 158, 55, 0.1)";
                              }}
                            />
                            {errors.message && touched.message && (
                              <div
                                style={{
                                  color: "#dc2626",
                                  fontSize: "13px",
                                  marginTop: "6px",
                                }}
                              >
                                <i className="ri-error-warning-line me-1"></i>
                                {errors.message}
                              </div>
                            )}
                          </FormGroup>

                          {/* Submit Button */}
                          <Button
                            type="submit"
                            disabled={isSubmitting || isLoading}
                            style={{
                              height: "44px",
                              backgroundColor: "#2F9E37",
                              borderColor: "#2F9E37",
                              borderRadius: "22px",
                              fontWeight: "600",
                              fontSize: "14px",
                              padding: "0 32px",
                              display: "inline-flex",
                              alignItems: "center",
                              justifyContent: "center",
                              minWidth: "160px",
                              border: "none",
                              transition: "all 0.2s ease",
                            }}
                            onMouseEnter={(e) => {
                              if (!isLoading && !isSubmitting) {
                                e.target.style.backgroundColor = "#268a2f";
                                e.target.style.transform = "translateY(-1px)";
                              }
                            }}
                            onMouseLeave={(e) => {
                              if (!isLoading && !isSubmitting) {
                                e.target.style.backgroundColor = "#2F9E37";
                                e.target.style.transform = "translateY(0)";
                              }
                            }}
                          >
                            {isLoading ? (
                              <>
                                <Spinner size="sm" className="me-2" />
                                Sending...
                              </>
                            ) : (
                              "Send Message"
                            )}
                          </Button>
                        </Form>
                      )}
                    </Formik>
                  </Col>

                  {/* Right Side - Social Media */}
                  <Col lg={2} md={5}>
                    <div
                      className="d-flex flex-column align-items-center justify-content-center h-100"
                      //   style={{ minHeight: "300px" }}
                    >
                      <div className="d-flex flex-column gap-4">
                        {/* LinkedIn */}
                        <a
                          href="https://www.linkedin.com/company/eirnabio"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="d-flex align-items-center justify-content-center"
                          style={{
                            width: "56px",
                            height: "56px",
                            backgroundColor: "#0077b5",
                            borderRadius: "50%",
                            color: "#ffffff",
                            textDecoration: "none",
                            border: "2px solid #0077b5",
                          }}
                        >
                          <i
                            className="ri-linkedin-fill"
                            style={{ fontSize: "22px" }}
                          ></i>
                        </a>

                        {/* X (formerly Twitter) */}
                        <a
                          href="https://x.com/EIRNABio"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="d-flex align-items-center justify-content-center"
                          style={{
                            width: "56px",
                            height: "56px",
                            backgroundColor: "#000000",
                            borderRadius: "50%",
                            color: "#ffffff",
                            textDecoration: "none",
                            border: "2px solid #000000",
                          }}
                        >
                          <i
                            className="ri-twitter-x-fill"
                            style={{ fontSize: "22px" }}
                          ></i>
                        </a>
                      </div>
                    </div>
                  </Col>
                </Row>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default ContactUs;
